import { Injectable, Injector, NgZone, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Router } from '@angular/router';
import { FirebaseApp, deleteApp, initializeApp } from 'firebase/app';
import {
  Messaging,
  MessagePayload,
  deleteToken,
  getMessaging,
  getToken,
  isSupported,
  onMessage,
} from 'firebase/messaging';
import { environment } from '../../environments/environment';
import { ServiceConstant } from '../utils/service-constant.util';
import { NotificationService } from './notification.service';
import { RestaurantService } from './restaurant.service';
import { SubscriptionService } from './subscription.service';
import { ReservationService } from './reservation.service';
import { AuthService } from './auth.service';
import { ToastService } from './toast.service';
import { dashboardRoute } from '../utils/notification-format.util';

/** Where this browser's identity is kept, so a refreshed token replaces a row. */
const DEVICE_ID_KEY = 'fnf_web_device_id';

/** The event that unlocks the rest of the dashboard for a published restaurant. */
const RESTAURANT_ACTIVATED = 'RESTAURANT_ACTIVATED';
/** Any other status move; activated → published puts the locks back. */
const RESTAURANT_STATUS_CHANGED = 'RESTAURANT_STATUS_CHANGED';

/** A guest asked to book and staff must accept or decline (FNF-308). */
const RESERVATION_REQUESTED = 'RESERVATION_REQUESTED';

/** Statuses staff cannot be signed in under — login only works on activated and published. */
const SIGNED_OUT_STATUSES = new Set(['created', 'deactivated', 'deleted']);

/** Events after which the subscription on the server no longer matches the tab. */
const BILLING_EVENTS = new Set([
  'SUBSCRIPTION_UPGRADED',
  'SUBSCRIPTION_DOWNGRADED',
  'SUBSCRIPTION_RENEWED',
  'SUBSCRIPTION_PAST_DUE',
  'SUBSCRIPTION_SUSPENDED',
  'SUBSCRIPTION_CANCELLED',
  'PAYMENT_RECEIVED',
  'TRIAL_ENDING',
]);

export type WebPushState = 'unsupported' | 'unconfigured' | 'default' | 'granted' | 'denied';

/**
 * Web push for the dashboard (FNF-415).
 *
 * The browser is registered with the same `/v1/notifications/fcm/tokens`
 * endpoint the mobile apps use, as `device_type: web`, `app: dashboard`, so
 * the notification service fans an event out to every device a person has.
 *
 * A message that arrives while a tab is focused comes through `onMessage` and
 * is shown as a toast — the browser suppresses its own banner for focused
 * tabs. Everything else is drawn by `firebase-messaging-sw.js`.
 */
@Injectable({ providedIn: 'root' })
export class WebPushService {
  private readonly http = inject(HttpClient);
  private readonly notifications = inject(NotificationService);
  private readonly toast = inject(ToastService);
  private readonly restaurants = inject(RestaurantService);
  private readonly subscriptions = inject(SubscriptionService);
  private readonly reservations = inject(ReservationService);
  // AuthService injects this service; resolved lazily to avoid the cycle.
  private readonly injector = inject(Injector);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);

  private app?: FirebaseApp;
  private messaging?: Messaging;
  private token: string | null = null;
  private starting = false;

  state: WebPushState = 'default';

  /** True when this build carries a Firebase web config to talk to. */
  get configured(): boolean {
    const config = environment.firebase;
    return !!config?.apiKey && !!config?.projectId && !!environment.firebaseVapidKey;
  }

  /**
   * Asks for permission if it has not been answered, then registers the token.
   * Idempotent per session, and safe to call when the browser cannot do push
   * or the build has no Firebase config — it just records why and returns.
   */
  async start(): Promise<WebPushState> {
    if (this.starting || this.token) return this.state;
    this.starting = true;
    try {
      if (!this.configured) {
        this.state = 'unconfigured';
        return this.state;
      }
      if (!(await isSupported())) {
        this.state = 'unsupported';
        return this.state;
      }

      // Safari and Firefox only grant permission from a user gesture; the
      // browser tells us straight away when it will not ask.
      const permission =
        Notification.permission === 'default'
          ? await Notification.requestPermission()
          : Notification.permission;
      this.state = permission === 'granted' ? 'granted' : 'denied';
      if (permission !== 'granted') return this.state;

      const registration = await this.registerServiceWorker();
      this.app = initializeApp(environment.firebase!, 'dashboard-push');
      this.messaging = getMessaging(this.app);

      const token = await this.mintRegisteredToken(registration);
      if (!token) return this.state;
      this.token = token;

      onMessage(this.messaging, (payload) => this.zone.run(() => this.onForeground(payload)));
      this.listenForBackgroundMessages();
      return this.state;
    } catch (error) {
      console.error('Web push could not start:', error);
      return this.state;
    } finally {
      this.starting = false;
    }
  }

  /** Drops the token on logout, while the bearer token can still authenticate. */
  async stop(): Promise<void> {
    const token = this.token;
    this.token = null;
    try {
      if (token) {
        await this.unregister(token);
        await this.dropToken();
      }
    } catch (error) {
      console.error('Web push could not be stopped cleanly:', error);
    } finally {
      if (this.app) {
        await deleteApp(this.app).catch(() => undefined);
        this.app = undefined;
        this.messaging = undefined;
      }
    }
  }

  /**
   * A focused tab gets no browser banner, so the message is surfaced as a
   * toast and the badge is refreshed. Clicking the toast opens where the
   * notification points — query string included, so a receipt notification
   * lands on its invoice.
   *
   * `RESTAURANT_ACTIVATED` also refetches the restaurant: the status guard
   * and the sidebar locks read `currentRestaurantDetails$`, and the tab that
   * was open when the restaurant went live would otherwise stay locked until
   * a reload.
   */
  private onForeground(payload: MessagePayload): void {
    const data = (payload.data ?? {}) as Record<string, string>;
    const title = payload.notification?.title ?? titleFrom(data);
    const body = payload.notification?.body ?? '';
    const route = dashboardRoute(decodePayload(data['payload'])) ?? '/main/notifications';

    this.notifications.refreshCounts();
    if (this.reactTo(eventOf(data), data) === 'signed-out') return;
    this.toast
      .info(body, title, { disableTimeOut: false, closeButton: true })
      .onTap.subscribe(() => this.router.navigateByUrl(route));
  }

  /**
   * State the rest of the app must re-read because of the event, whether or
   * not the toast is clicked. Activation → restaurant details (status guard
   * and sidebar locks). Plan or billing change → the subscription and the
   * plan name the sidebar and route guard gate on, plus a nudge to an open
   * Billing page — otherwise "Plan upgraded to Standard" arrives while the
   * page still says Free until a reload.
   */
  private reactTo(
    event: string | undefined,
    data: Record<string, string> = {},
  ): 'signed-out' | undefined {
    // Sign-out is decided before anything is refetched. Asking for a details
    // refresh first meant the sidebar's in-flight GET resolved after logout()
    // had already navigated to /login, and its own handler then navigated on
    // to /deactive-restaurant - the session was gone but the app never looked
    // signed out, which is what a deactivated restaurant reported.
    if (event === RESTAURANT_STATUS_CHANGED) {
      const status = String(decodePayload(data['payload'])?.['status'] ?? '').toLowerCase();
      if (SIGNED_OUT_STATUSES.has(status)) {
        this.signOutForStatus(status);
        return 'signed-out';
      }
    }
    if (event === RESTAURANT_ACTIVATED || event === RESTAURANT_STATUS_CHANGED) {
      this.restaurants.requestRestaurantDetailsRefresh();
    }
    if (event === RESTAURANT_STATUS_CHANGED) return undefined;
    // A new booking request (FNF-308): the sidebar badge and Requests queue
    // re-read now rather than at the next poll, and an open book refetches
    // its day through changes$, as after a write of its own.
    if (event === RESERVATION_REQUESTED) {
      this.reservations.refreshRequests();
      this.reservations.notifyChanged();
      return undefined;
    }
    // RESTAURANT_ACTIVATED falls through: activation is when the default
    // subscription appears, and both SubscriptionPlanGuard and the sidebar
    // locks gate on currentPlanName$. Refreshing only the restaurant left the
    // status at `activated` (so plan gating switches on) while the plan name
    // was still null, which locks the features activation was meant to open.
    if (!event || !(BILLING_EVENTS.has(event) || event === RESTAURANT_ACTIVATED)) {
      return undefined;
    }
    this.subscriptions.getMySubscription().subscribe({
      next: (response: any) => {
        const subscription = response?.data;
        if (!subscription) return;
        this.restaurants.restaurantSubscription$.next(subscription);
        if (!subscription.planId) return;
        this.subscriptions.getPlan(subscription.planId).subscribe({
          next: (plan: any) => {
            if (plan?.data?.name) this.restaurants.currentPlanName$.next(plan.data.name);
          },
          error: () => undefined,
        });
      },
      error: () => undefined,
    });
    this.restaurants.requestSubscriptionRefresh();
    return undefined;
  }

  /**
   * A tab that is open but hidden gets no `onMessage`. The SDK routes a push
   * to the foreground only while some client reports
   * `visibilityState === 'visible'` - focus is not part of the test, so a
   * visible window sitting behind another app still takes that path. When
   * every tab is hidden the service worker draws the banner instead and
   * forwards the data message here, so the tab still reacts: a deactivation
   * signs the session out rather than waiting for the next reload, and an
   * activation refreshes the plan behind the locks.
   *
   * No toast: the browser has already shown its own notification for this
   * message, and no one is looking at this tab.
   */
  private listenForBackgroundMessages(): void {
    if (!('serviceWorker' in navigator)) return;
    navigator.serviceWorker.addEventListener('message', (event: MessageEvent) => {
      const message = event.data as { type?: string; data?: Record<string, string> } | undefined;
      if (message?.type !== 'fnf-push') return;
      this.zone.run(() => {
        const data = message.data ?? {};
        this.notifications.refreshCounts();
        this.reactTo(eventOf(data), data);
      });
    });
  }

  /**
   * The restaurant can no longer be used by staff, so the session must not
   * outlive it: logout() also DELETEs this browser's push token while the
   * bearer is still valid.
   */
  private signOutForStatus(status: string): void {
    const reason =
      status === 'deleted'
        ? 'This restaurant has been removed from Fynkle.'
        : status === 'deactivated'
          ? 'This restaurant has been deactivated.'
          : 'This restaurant is back in setup.';
    this.toast.warning(`${reason} You can sign in again once it is live.`, 'Signed out', {
      disableTimeOut: false,
      closeButton: true,
    });
    this.injector.get(AuthService).logout();
    void this.router.navigateByUrl('/login');
  }

  /**
   * A token the browser hands back can already be dead at FCM. The push
   * subscription behind it is gone - a `deleteToken` that failed on the
   * previous sign-out leaves exactly that - while the SDK keeps returning the
   * same string from its IndexedDB, so the tab looks registered and never is.
   * notification-service dry-runs every token it is given and answers
   * "Invalid FCM token provided", which is how a browser stops being a push
   * target for good: a deactivation then signs every device out except this
   * one, because no message ever arrives here.
   *
   * So the answer is read rather than assumed, and a token the server refuses
   * is thrown away and minted again from a fresh subscription.
   */
  private async mintRegisteredToken(
    registration: ServiceWorkerRegistration,
  ): Promise<string | null> {
    for (let attempt = 0; attempt < 2; attempt++) {
      const token = await getToken(this.messaging!, {
        vapidKey: environment.firebaseVapidKey,
        serviceWorkerRegistration: registration,
      });
      if (!token) return null;
      if (await this.registerAccepted(token)) return token;
      console.warn('Push token rejected by the server; minting a new one.');
      await this.dropToken(registration);
    }
    return null;
  }

  /**
   * Whether the token was actually stored. The gateway answers 200 with the
   * upstream status inside the envelope, so the HTTP status alone says
   * nothing about whether notification-service kept the token.
   */
  private async registerAccepted(token: string): Promise<boolean> {
    try {
      const response = (await this.register(token)) as { status_code?: number } | undefined;
      const status = response?.status_code ?? 200;
      return status >= 200 && status < 300;
    } catch (error) {
      console.error('Push token could not be registered:', error);
      return false;
    }
  }

  /**
   * Forget this browser's token everywhere it is cached. `deleteToken` throws
   * when FCM has already dropped the registration, and on its own it leaves
   * the subscription and the SDK's database behind - the next `getToken` then
   * returns the same dead string. Unsubscribing and clearing the database is
   * what makes the next call mint a genuinely new token.
   */
  private async dropToken(registration?: ServiceWorkerRegistration): Promise<void> {
    try {
      if (this.messaging) await deleteToken(this.messaging);
    } catch {
      // Already gone at FCM; the local copies below are what matter.
    }
    try {
      const subscription = await (
        registration ?? (await navigator.serviceWorker.getRegistration('/'))
      )?.pushManager.getSubscription();
      if (subscription) await subscription.unsubscribe();
    } catch {
      // A browser that will not let go of the subscription still gets a fresh
      // database below, which is enough for the SDK to ask for a new token.
    }
    await deleteMessagingDatabase();
  }

  /**
   * The config travels on the registration URL because a service worker
   * cannot read Angular's environment files. Registering the same URL twice
   * is a no-op, so this is safe to call on every start.
   */
  private registerServiceWorker(): Promise<ServiceWorkerRegistration> {
    const config = environment.firebase!;
    const params = new URLSearchParams({
      apiKey: config.apiKey,
      authDomain: config.authDomain,
      projectId: config.projectId,
      messagingSenderId: config.messagingSenderId,
      appId: config.appId,
    });
    return navigator.serviceWorker.register(`/firebase-messaging-sw.js?${params.toString()}`, {
      scope: '/',
    });
  }

  private register(token: string): Promise<unknown> {
    return this.http
      .post(`${ServiceConstant.apiUrl}/notifications/fcm/tokens`, {
        token,
        device_type: 'web',
        device_id: deviceId(),
        app: 'dashboard',
      })
      .toPromise();
  }

  private unregister(token: string): Promise<unknown> {
    return this.http
      .delete(`${ServiceConstant.apiUrl}/notifications/fcm/tokens`, {
        params: new HttpParams().set('token', token),
      })
      .toPromise();
  }
}

/** Stable per browser, so a refreshed token replaces its row rather than adding one. */
function deviceId(): string {
  let id = localStorage.getItem(DEVICE_ID_KEY);
  if (!id) {
    id = `web-${crypto.randomUUID()}`;
    localStorage.setItem(DEVICE_ID_KEY, id);
  }
  return id;
}

/**
 * Drops the messaging SDK's own store. Resolves on `blocked` too: another tab
 * holding the database open must not stall sign-in, and the token it caches
 * is refreshed the next time that tab starts.
 */
function deleteMessagingDatabase(): Promise<void> {
  return new Promise<void>((resolve) => {
    try {
      const request = indexedDB.deleteDatabase('firebase-messaging-database');
      request.onsuccess = request.onerror = request.onblocked = () => resolve();
    } catch {
      resolve();
    }
  });
}

function decodePayload(raw: string | undefined): Record<string, string> | undefined {
  if (!raw) return undefined;
  try {
    const parsed = JSON.parse(raw);
    return typeof parsed === 'object' && parsed ? (parsed as Record<string, string>) : undefined;
  } catch {
    return undefined;
  }
}

/** The event name a data message carries, upper-cased the way the catalogue spells it. */
function eventOf(data: Record<string, string>): string {
  return (data['event'] ?? data['notification_type'] ?? '').toUpperCase();
}

/** Title for a data-only message, from its event name. */
function titleFrom(data: Record<string, string>): string {
  const event = data['event'] ?? data['notification_type'] ?? '';
  if (!event) return 'New notification';
  return event
    .split(/[._\s]+/)
    .filter(Boolean)
    .map((word) => word[0].toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}
